export interface Volume {
  icon: string
  number: string
  title: string
  subtitle: string
  problem: string
  bullets: string[]
  result: string
  popular?: boolean
}

export const volumes: Volume[] = [
  {
    icon: '📘',
    number: 'Volume 1',
    title: 'Taekwondo Foundations & Instructor Methodology',
    subtitle: 'Build the professional foundation behind every successful Taekwondo class',
    problem:
      'Teaching good Taekwondo starts long before advanced kicks or sparring. This volume gives instructors a complete foundation for organizing safe, structured and progressive classes. It also includes ready-to-use foundation classes so instructors can immediately turn principles into practice.',
    bullets: [
      'Stance and posture, guard position and balance',
      'Basic footwork and fundamental hand techniques',
      'Blocking fundamentals and introductory kicking',
      'Warm-up structure and class organization',
      'Coaching methodology, common-error correction and beginner development',
    ],
    result:
      'Your students develop stronger fundamentals, and you teach them through a repeatable professional methodology instead of improvising every class.',
  },
  {
    icon: '🦵',
    number: 'Volume 2',
    title: 'Kicking Mechanics, Footwork & Technical Progressions',
    subtitle:
      'A complete visual coaching system for developing controlled, powerful and progressive kicking skills',
    problem:
      'Kicking is one of the most important areas of Taekwondo — and one of the easiest to teach poorly when progressions are unclear. This volume breaks kicking development into a structured system: Set → Chamber → Pivot → Extend → Contact → Recoil → Recover.',
    bullets: [
      'Front kick, roundhouse kick, side kick, back kick and hook kick',
      'Supporting-foot mechanics, hip rotation and balance',
      'Accuracy, recoil and footwork',
      'Pad work and combination kicking',
      'Beginner-to-advanced kicking progressions and coaching corrections',
    ],
    result:
      'You stop teaching kicks as isolated movements and start developing them through a clear progression your students can understand and repeat.',
  },
  {
    icon: '🥋',
    number: 'Volume 3',
    title: 'Hand Techniques, Blocks, Stances & Combination Systems',
    subtitle: 'Turn isolated techniques into coordinated Taekwondo movement',
    problem:
      'This volume gives instructors a professional system for teaching hand techniques, defensive movements and combinations. Core coaching system: Position → Path → Contact → Recover → Combine → Apply.',
    bullets: [
      'Stance mechanics, stance transitions and weight transfer',
      'Punching mechanics, hand strikes and blocking systems',
      'Block-counter development and hand-and-kick combinations',
      'Footwork integration, pad drills and reaction drills',
      'Correct-vs-incorrect technique and ready-to-teach technical classes',
    ],
    result:
      'Your students stop performing disconnected movements and begin building coordinated, usable technique.',
  },
  {
    icon: '🎽',
    number: 'Volume 4',
    title: 'Poomsae, Forms & Technical Performance System',
    subtitle: 'Teach forms with structure, precision and progression',
    problem:
      'Poomsae should not feel like memorizing movements without understanding them. Core teaching system: Learn → Break Down → Connect → Correct → Perform → Review. Includes correction drills, class plans and performance tools.',
    bullets: [
      'Movement sequences, direction and stance precision',
      'Turning mechanics, blocks and strikes',
      'Rhythm, breathing and technical end positions',
      'Memory, segmentation and performance quality',
      'Correction and beginner-to-advanced development',
    ],
    result:
      'Your students perform forms with greater control, understanding and technical consistency.',
  },
  {
    icon: '🤺',
    number: 'Volume 5',
    title: 'Sparring, Distance, Timing & Tactical Development',
    subtitle: 'Build smarter sparring — not random fighting',
    problem:
      'This volume helps instructors create structured sparring development from technical preparation to controlled tactical application. Core system: Position → Read → Move → Attack or Counter → Exit → Reset.',
    bullets: [
      'Fighting stance, footwork and distance control',
      'Entry and exit, timing windows and reaction',
      'Attack systems, counterattack systems, feints and setups',
      'Tactical decision-making and combination systems',
      'Controlled sparring formats and beginner-to-advanced sparring progression',
    ],
    result:
      'Your sparring sessions become structured learning environments where students develop decisions, not just speed.',
  },
  {
    icon: '📚',
    number: 'Volume 6',
    title: '150 Ready-to-Teach Taekwondo Sessions',
    subtitle: 'Stop building every class from scratch',
    problem:
      'This is the practical heart of the system. You receive 150 complete ready-to-teach Taekwondo sessions, organized across the major areas of instruction. Select sessions according to student level, training objective, duration, equipment, technical focus and class conditions. Open → Choose → Teach.',
    bullets: [
      '20 Foundations & Beginner Development Sessions',
      '25 Kicking Mechanics & Development Sessions · 15 Hand Techniques, Blocks & Combination Sessions',
      '20 Poomsae & Technical Performance Sessions · 15 Footwork & Distance Control Sessions',
      '25 Sparring & Tactical Development Sessions',
      '15 Conditioning, Agility & Movement Sessions · 15 Mixed-Skill & Advanced Integration Sessions',
    ],
    result:
      'Instead of wondering what to teach tomorrow, you open the library and start with a complete session structure.',
    popular: true,
  },
  {
    icon: '🏅',
    number: 'Volume 7',
    title: 'Advanced Coaching, Student Development & Instructor Toolkit',
    subtitle:
      'Stop thinking only about the next class. Start developing the next stage of the student.',
    problem:
      'The final volume takes the system beyond individual sessions. Core system: Assess → Plan → Teach → Observe → Adapt → Review → Develop.',
    bullets: [
      'Student development pathways and next-skill decision systems',
      'Weekly programming, monthly programming and 8–12 week training blocks',
      'Mixed-level class management and student assessment',
      'Coaching cues, correction systems and class quality control',
      'Evaluation preparation, advanced coaching scenarios and professional instructor tools',
    ],
    result:
      'You stop managing isolated classes and begin building a structured student-development pathway.',
  },
]
